import { useState, useEffect, useMemo, memo } from 'react';
import axios from 'axios';
import Barcode from 'react-barcode';
import { Printer, CheckSquare, Square, Settings2, Eye, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Checkbox } from './ui/checkbox';
import { ScrollArea } from './ui/scroll-area';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { getColorInfoForBike } from './PriceColorSystem';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Predefined label sizes
const LABEL_SIZES = [
  { id: 'topstick-8731', name: 'Topstick 8731 (48.3×25.4mm, A4)', width: 48.3, height: 25.4, template: 'a4grid', cols: 4, rows: 10, marginLeft: 8.1, marginTop: 21.2 },
  { id: '48x25', name: '48.3 x 25.4 mm (individual)', width: 48.3, height: 25.4 },
  { id: '50x25', name: '50 x 25 mm', width: 50, height: 25 },
  { id: '60x30', name: '60 x 30 mm', width: 60, height: 30 },
  { id: 'custom', name: 'Personalizado', width: 0, height: 0 },
];

// Convert internal code to barcode value - use raw code (CODE128 supports all ASCII)
const toBarcodeValue = (code) => code || 'NOCODE';

// Extract talla and sequential number for label display
// From "2(53)16" → talla=53, numero=16 (just the sequential unit number)
const parseInternalCode = (bicycle) => {
  const code = bicycle.number || bicycle.internal_code;
  const talla = bicycle.size || '--';
  if (!code) return { size: talla, number: '--' };
  // Match pattern: modelNum(talla)sequence → extract only sequence
  const match = code.match(/\)(\d+)$/);
  if (match) {
    return { size: talla, number: match[1].padStart(2, '0') };
  }
  return { size: talla, number: code };
};

// Single label preview component with new design - memoized to prevent re-renders
const BicycleLabel = memo(function BicycleLabel({ bicycle, yearColors, labelWidth, labelHeight, showDot = true }) {
  const colorInfo = getColorInfoForBike(bicycle.model_year, yearColors);
  const { size, number } = parseInternalCode(bicycle);
  const barcodeValue = toBarcodeValue(bicycle.number || bicycle.internal_code);
  
  const dotSize = 5;
  const codeSize = 14;
  const barcodeHeight = 20;
  const barcodeWidth = 1.1;
  
  return (
    <div 
      className="label-single"
      style={{
        width: `${labelWidth}mm`,
        height: `${labelHeight}mm`,
        padding: '1.2mm 1.5mm',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: showDot ? 'space-between' : 'center',
        alignItems: 'center',
        gap: showDot ? undefined : '1mm',
        border: '0.3mm dashed #ccc',
        backgroundColor: 'white',
        pageBreakInside: 'avoid',
        overflow: 'hidden'
      }}
    >
      {showDot && (
        <div style={{
          width: `${dotSize}mm`,
          height: `${dotSize}mm`,
          borderRadius: '50%',
          backgroundColor: colorInfo.hex,
          border: '0.3mm solid rgba(0,0,0,0.2)',
          flexShrink: 0
        }} />
      )}
      
      <div style={{
        fontSize: `${codeSize}pt`,
        fontWeight: 'bold',
        fontFamily: 'Arial, sans-serif',
        textAlign: 'center',
        letterSpacing: '1px',
        lineHeight: 1.1
      }}>
        {size} / {number}
      </div>
      
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        overflow: 'hidden'
      }}>
        <Barcode 
          value={barcodeValue}
          format="CODE128"
          width={barcodeWidth}
          height={barcodeHeight}
          displayValue={false}
          margin={0}
        />
      </div>
    </div>
  );
});

export default function PrintLabelModal({ 
  open, onClose, 
  bicycle = null,
  bicycles = [],
  preSelectedIds = []
}) {
  const [printMode, setPrintMode] = useState('all');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set(preSelectedIds));
  const [allBicycles, setAllBicycles] = useState(bicycles);
  const [yearColors, setYearColors] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Label size configuration
  const [labelSizeId, setLabelSizeId] = useState('topstick-8731');
  const [customWidth, setCustomWidth] = useState(50);
  const [customHeight, setCustomHeight] = useState(25);
  const [showSizeConfig, setShowSizeConfig] = useState(false);
  const [showColorDot, setShowColorDot] = useState(true);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [showFullPreview, setShowFullPreview] = useState(false);

  // Get current label dimensions - memoized
  const labelDimensions = useMemo(() => {
    if (labelSizeId === 'custom') {
      return { width: customWidth, height: customHeight };
    }
    const preset = LABEL_SIZES.find(s => s.id === labelSizeId);
    return preset ? { width: preset.width, height: preset.height } : { width: 48.3, height: 25.4 };
  }, [labelSizeId, customWidth, customHeight]);

  const selectedTemplate = useMemo(() => LABEL_SIZES.find(s => s.id === labelSizeId), [labelSizeId]);

  // Fetch bicycles and year colors on open
  useEffect(() => {
    if (!open) return;
    const token = localStorage.getItem('veloflux_token');
    const headers = { Authorization: `Bearer ${token}` };
    
    // Fetch year colors
    axios.get(`${API}/config/year-colors`, { headers })
      .then(res => setYearColors(res.data.colors))
      .catch(() => {});

    if (bicycles.length === 0) {
      setLoading(true);
      axios.get(`${API}/bicycles?status=activa`, { headers })
        .then(res => setAllBicycles(res.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    } else {
      setAllBicycles(bicycles);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bicycles.length]);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setShowFullPreview(false);
      if (bicycle) {
        setPrintMode('single');
        setSelectedIds(new Set([bicycle.id]));
      } else if (preSelectedIds.length > 0) {
        setPrintMode('selection');
        setSelectedIds(new Set(preSelectedIds));
      } else {
        setPrintMode('all');
        setSelectedIds(new Set());
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, bicycle?.id, preSelectedIds.length]);

  const models = [...new Set(allBicycles.map(b => b.model).filter(Boolean))].sort();
  const sizes = [...new Set(allBicycles.map(b => b.size).filter(Boolean))].sort();

  const getBicyclesToPrint = () => {
    switch (printMode) {
      case 'all': return allBicycles;
      case 'single': return bicycle ? [bicycle] : [];
      case 'model': return allBicycles.filter(b => b.model === selectedModel);
      case 'size': return allBicycles.filter(b => b.size === selectedSize);
      case 'selection': return allBicycles.filter(b => selectedIds.has(b.id));
      default: return [];
    }
  };

  const bicyclesToPrint = getBicyclesToPrint();

  const toggleSelection = (id) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) newSet.delete(id); else newSet.add(id);
    setSelectedIds(newSet);
  };

  const selectAll = () => setSelectedIds(new Set(allBicycles.map(b => b.id)));
  const selectNone = () => setSelectedIds(new Set());

  const handlePrint = () => {
    if (bicyclesToPrint.length === 0) return;
    const colors = yearColors || [];
    const { width: labelWidth, height: labelHeight } = labelDimensions;
    const tpl = selectedTemplate;
    const isA4Grid = tpl?.template === 'a4grid';
    
    const dotSize = 5;
    const codeSize = 14;
    const barcodeHeight = 20;
    const barcodeWidth = 1.1;
    const ox = offsetX || 0;
    const oy = offsetY || 0;

    const labelsHTML = bicyclesToPrint.map((bike, idx) => {
      const colorInfo = getColorInfoForBike(bike.model_year, colors);
      const { size, number } = parseInternalCode(bike);
      const barcodeValue = toBarcodeValue(bike.number || bike.internal_code).replace(/'/g, "\\'");
      const dotHTML = showColorDot ? `<div class="color-dot" style="background-color: ${colorInfo.hex}; width: ${dotSize}mm; height: ${dotSize}mm;"></div>` : '';
      
      if (isA4Grid) {
        const col = idx % tpl.cols;
        const row = Math.floor(idx / tpl.cols) % tpl.rows;
        const left = tpl.marginLeft + col * labelWidth + ox;
        const top = tpl.marginTop + row * labelHeight + oy;
        const isFirstOfPage = idx > 0 && idx % (tpl.cols * tpl.rows) === 0;
        return `${isFirstOfPage ? '<div class="page-break"></div>' : ''}
          <div class="label" style="position:absolute; left:${left}mm; top:${top}mm;">
            ${dotHTML}
            <div class="code">${size} / ${number}</div>
            <div class="barcode"><svg id="barcode-${bike.id}"></svg></div>
          </div>`;
      }
      return `<div class="label">${dotHTML}<div class="code">${size} / ${number}</div><div class="barcode"><svg id="barcode-${bike.id}"></svg></div></div>`;
    }).join('');

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const totalPages = isA4Grid ? Math.ceil(bicyclesToPrint.length / (tpl.cols * tpl.rows)) : 1;
    const pagesHTML = isA4Grid
      ? Array.from({ length: totalPages }, (_, pageIdx) => {
          const startIdx = pageIdx * tpl.cols * tpl.rows;
          const pageLabels = bicyclesToPrint.slice(startIdx, startIdx + tpl.cols * tpl.rows);
          return `<div class="a4-page">${pageLabels.map((bike, i) => {
            const globalIdx = startIdx + i;
            const col = globalIdx % tpl.cols;
            const row = Math.floor(globalIdx / tpl.cols) % tpl.rows;
            const left = tpl.marginLeft + col * labelWidth + ox;
            const top = tpl.marginTop + row * labelHeight + oy;
            const colorInfo = getColorInfoForBike(bike.model_year, colors);
            const { size, number } = parseInternalCode(bike);
            const dotHTML = showColorDot ? `<div class="color-dot" style="background-color: ${colorInfo.hex}; width: ${dotSize}mm; height: ${dotSize}mm;"></div>` : '';
            return `<div class="label" style="position:absolute; left:${left}mm; top:${top}mm;">${dotHTML}<div class="code">${size} / ${number}</div><div class="barcode"><svg id="barcode-${bike.id}"></svg></div></div>`;
          }).join('')}</div>`;
        }).join('')
      : `<div class="labels-container">${labelsHTML}</div>`;

    printWindow.document.write(`<!DOCTYPE html><html><head>
      <title>Etiquetas de Bicicletas</title>
      <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
      <style>
        @page { size: A4; margin: 0; }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html, body { width: 210mm; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        body { font-family: Arial, sans-serif; }
        ${isA4Grid ? `
        .a4-page { width: 210mm; height: 297mm; position: relative; page-break-after: always; }
        .a4-page:last-child { page-break-after: auto; }
        .label {
          width: ${labelWidth}mm; height: ${labelHeight}mm;
          padding: 1.2mm 1.5mm; display: flex; flex-direction: column;
          justify-content: ${showColorDot ? 'space-between' : 'center'}; align-items: center;
          ${showColorDot ? '' : 'gap: 1mm;'}
          overflow: hidden;
        }
        ` : `
        .labels-container { display: flex; flex-wrap: wrap; gap: 0mm; padding: 3mm; }
        .label {
          width: ${labelWidth}mm; height: ${labelHeight}mm;
          padding: 1.2mm 1.5mm; display: flex; flex-direction: column;
          justify-content: ${showColorDot ? 'space-between' : 'center'}; align-items: center;
          ${showColorDot ? '' : 'gap: 1mm;'}
          page-break-inside: avoid; overflow: hidden;
        }
        `}
        .color-dot { border-radius: 50%; border: 0.3mm solid rgba(0,0,0,0.2); flex-shrink: 0; }
        .code { font-size: ${codeSize}pt; font-weight: bold; text-align: center; letter-spacing: 1px; line-height: 1.1; }
        .barcode { display: flex; justify-content: center; width: 100%; }
        .barcode svg { max-width: 100%; height: auto; }
      </style></head><body>
      ${pagesHTML}
      <script>
        ${bicyclesToPrint.map(bike => {
          const barcodeValue = toBarcodeValue(bike.number || bike.internal_code).replace(/'/g, "\\'");
          return `try { JsBarcode("#barcode-${bike.id}", '${barcodeValue}', { format: "CODE128", width: ${barcodeWidth}, height: ${barcodeHeight}, displayValue: false, margin: 0 }); } catch(e) {}`;
        }).join('\n')}
        setTimeout(() => { window.print(); }, 500);
      <\/script></body></html>`);
    printWindow.document.close();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={`${showFullPreview ? 'sm:max-w-4xl' : 'sm:max-w-2xl'} max-h-[90vh] overflow-hidden flex flex-col`} data-testid="print-label-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {showFullPreview ? (
              <>
                <Eye className="w-5 h-5 text-blue-500" />
                Vista previa de impresión
                <Badge variant="outline" className="text-xs ml-2">
                  {bicyclesToPrint.length} etiqueta{bicyclesToPrint.length !== 1 ? 's' : ''}
                </Badge>
              </>
            ) : (
              <>
                <Printer className="w-5 h-5 text-orange-500" />
                Imprimir Etiquetas
                <span className="text-sm font-normal text-slate-500">
                  ({labelDimensions.width}x{labelDimensions.height}mm)
                </span>
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-4 py-2">
          {!showFullPreview && (
          <>
          {/* Label size configuration */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Settings2 className="w-4 h-4" />
                Tamaño de etiqueta
              </Label>
              <button
                className="text-xs text-blue-600 hover:underline"
                onClick={() => setShowSizeConfig(!showSizeConfig)}
              >
                {showSizeConfig ? 'Ocultar opciones' : 'Mostrar opciones'}
              </button>
            </div>
            
            {showSizeConfig && (
              <div className="p-3 bg-slate-50 rounded-lg border space-y-3">
                <Select value={labelSizeId} onValueChange={(v) => { setLabelSizeId(v); setShowFullPreview(false); }}>
                  <SelectTrigger data-testid="label-size-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LABEL_SIZES.map(size => (
                      <SelectItem key={size.id} value={size.id}>
                        {size.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Color dot toggle */}
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Mostrar punto de color del año</Label>
                  <Switch checked={showColorDot} onCheckedChange={setShowColorDot} data-testid="toggle-color-dot" />
                </div>

                {/* Offset */}
                <div className="flex gap-3 items-center">
                  <div className="flex-1">
                    <Label className="text-xs text-slate-500">Offset X (mm)</Label>
                    <Input type="number" step="0.1" value={offsetX} onChange={e => setOffsetX(parseFloat(e.target.value) || 0)} className="h-8" data-testid="offset-x-input" />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs text-slate-500">Offset Y (mm)</Label>
                    <Input type="number" step="0.1" value={offsetY} onChange={e => setOffsetY(parseFloat(e.target.value) || 0)} className="h-8" data-testid="offset-y-input" />
                  </div>
                </div>
                
                {labelSizeId === 'custom' && (
                  <div className="flex gap-3 items-center">
                    <div className="flex-1">
                      <Label className="text-xs text-slate-500">Ancho (mm)</Label>
                      <Input
                        type="number"
                        min="20"
                        max="200"
                        value={customWidth}
                        onChange={(e) => setCustomWidth(parseFloat(e.target.value) || 50)}
                        className="h-8"
                        data-testid="custom-width-input"
                      />
                    </div>
                    <span className="text-slate-400 pt-4">×</span>
                    <div className="flex-1">
                      <Label className="text-xs text-slate-500">Alto (mm)</Label>
                      <Input
                        type="number"
                        min="10"
                        max="100"
                        value={customHeight}
                        onChange={(e) => setCustomHeight(parseFloat(e.target.value) || 25)}
                        className="h-8"
                        data-testid="custom-height-input"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Print mode selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Modo de impresión</Label>
            <RadioGroup value={printMode} onValueChange={setPrintMode} className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {[
                { value: 'all', label: 'Todas', desc: `${allBicycles.length} bicis` },
                ...(bicycle ? [{ value: 'single', label: 'Una bicicleta', desc: `${parseInternalCode(bicycle).size} / ${parseInternalCode(bicycle).number}` }] : []),
                { value: 'model', label: 'Por modelo', desc: 'Todas de un modelo' },
                { value: 'size', label: 'Por talla', desc: 'Todas de una talla' },
                { value: 'selection', label: 'Selección', desc: `${selectedIds.size} seleccionadas` },
              ].map(opt => (
                <div key={opt.value}
                  className={`flex items-center space-x-2 p-3 border rounded-lg cursor-pointer transition-colors ${printMode === opt.value ? 'border-orange-500 bg-orange-50' : 'hover:bg-slate-50'}`}>
                  <RadioGroupItem value={opt.value} id={`pm-${opt.value}`} />
                  <Label htmlFor={`pm-${opt.value}`} className="cursor-pointer flex-1">
                    <div className="font-medium text-sm">{opt.label}</div>
                    <div className="text-xs text-slate-500">{opt.desc}</div>
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Filter: by model */}
          {printMode === 'model' && (
            <div className="space-y-2">
              <Label>Seleccionar modelo</Label>
              <Select value={selectedModel} onValueChange={setSelectedModel}>
                <SelectTrigger data-testid="label-model-select">
                  <SelectValue placeholder="Elegir modelo..." />
                </SelectTrigger>
                <SelectContent>
                  {models.map(model => (
                    <SelectItem key={model} value={model}>
                      {model} ({allBicycles.filter(b => b.model === model).length} bicis)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Filter: by size */}
          {printMode === 'size' && (
            <div className="space-y-2">
              <Label>Seleccionar talla</Label>
              <Select value={selectedSize} onValueChange={setSelectedSize}>
                <SelectTrigger data-testid="label-size-select">
                  <SelectValue placeholder="Elegir talla..." />
                </SelectTrigger>
                <SelectContent>
                  {sizes.map(size => (
                    <SelectItem key={size} value={size}>
                      Talla {size} ({allBicycles.filter(b => b.size === size).length} bicis)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Filter: manual selection */}
          {printMode === 'selection' && (
            <div className="space-y-2 flex-1 overflow-hidden flex flex-col">
              <div className="flex items-center justify-between">
                <Label>Seleccionar bicicletas</Label>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={selectAll} data-testid="select-all-labels">
                    <CheckSquare className="w-4 h-4 mr-1" /> Todas
                  </Button>
                  <Button variant="ghost" size="sm" onClick={selectNone} data-testid="select-none-labels">
                    <Square className="w-4 h-4 mr-1" /> Ninguna
                  </Button>
                </div>
              </div>
              <ScrollArea className="flex-1 border rounded-lg p-2 max-h-48">
                <div className="space-y-1">
                  {loading ? (
                    <div className="text-center py-4 text-slate-500">Cargando...</div>
                  ) : (
                    allBicycles.map(bike => {
                      const ci = getColorInfoForBike(bike.model_year, yearColors);
                      const { size, number } = parseInternalCode(bike);
                      return (
                        <div key={bike.id}
                          className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${selectedIds.has(bike.id) ? 'bg-orange-50' : 'hover:bg-slate-50'}`}
                          onClick={() => toggleSelection(bike.id)}>
                          <Checkbox checked={selectedIds.has(bike.id)} />
                          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: ci.hex }} />
                          <span className="text-sm font-medium">{bike.model}</span>
                          <span className="text-sm text-slate-500">{bike.size}</span>
                          <code className="text-xs bg-slate-100 px-1 rounded ml-auto">
                            {size} / {number}
                          </code>
                        </div>
                      );
                    })
                  )}
                </div>
              </ScrollArea>
            </div>
          )}

          </>
          )}

          {/* Full sheet preview */}
          {showFullPreview && (
            <div className="flex-1 overflow-auto" data-testid="full-sheet-preview">
              <div className="text-sm text-slate-600 mb-3 text-center">
                Verifica que las etiquetas estén correctamente posicionadas antes de imprimir.
                <div className="text-xs text-amber-600 mt-1 font-medium">
                  Importante: Configura "Escala: 100%" en el diálogo de impresión del navegador.
                </div>
              </div>
              <div className="flex flex-col items-center gap-6 pb-4">
                {selectedTemplate?.template === 'a4grid' ? (
                  Array.from({ length: Math.ceil(bicyclesToPrint.length / (selectedTemplate.cols * selectedTemplate.rows)) }, (_, pageIdx) => {
                    const perPage = selectedTemplate.cols * selectedTemplate.rows;
                    const startIdx = pageIdx * perPage;
                    const pageLabels = bicyclesToPrint.slice(startIdx, startIdx + perPage);
                    return (
                      <div key={pageIdx} className="flex flex-col items-center">
                        <div className="text-xs text-slate-400 mb-1">
                          Página {pageIdx + 1} de {Math.ceil(bicyclesToPrint.length / perPage)}
                        </div>
                        <div
                          style={{
                            width: `${210 * 0.45}mm`,
                            height: `${297 * 0.45}mm`,
                            overflow: 'hidden',
                            border: '2px solid #cbd5e1',
                            borderRadius: '6px',
                            position: 'relative',
                            backgroundColor: '#f1f5f9'
                          }}
                        >
                          <div
                            style={{
                              width: '210mm',
                              height: '297mm',
                              position: 'relative',
                              backgroundColor: 'white',
                              transform: 'scale(0.45)',
                              transformOrigin: 'top left'
                            }}
                          >
                            {pageLabels.map((bike, i) => {
                              const col = i % selectedTemplate.cols;
                              const row = Math.floor(i / selectedTemplate.cols);
                              const left = selectedTemplate.marginLeft + col * labelDimensions.width + (offsetX || 0);
                              const top = selectedTemplate.marginTop + row * labelDimensions.height + (offsetY || 0);
                              return (
                                <div key={bike.id} style={{ position: 'absolute', left: `${left}mm`, top: `${top}mm` }}>
                                  <BicycleLabel
                                    bicycle={bike}
                                    yearColors={yearColors}
                                    labelWidth={labelDimensions.width}
                                    labelHeight={labelDimensions.height}
                                    showDot={showColorDot}
                                  />
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="flex flex-wrap gap-2 justify-center p-4 bg-white border rounded-lg">
                    {bicyclesToPrint.map(bike => (
                      <BicycleLabel
                        key={bike.id}
                        bicycle={bike}
                        yearColors={yearColors}
                        labelWidth={labelDimensions.width}
                        labelHeight={labelDimensions.height}
                        showDot={showColorDot}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2 border-t">
            {!showFullPreview ? (
              <>
                <Button variant="outline" className="flex-1" onClick={onClose} data-testid="print-labels-cancel-btn">
                  Cancelar
                </Button>
                <Button
                  className="flex-1 bg-orange-500 hover:bg-orange-600"
                  onClick={() => setShowFullPreview(true)}
                  disabled={bicyclesToPrint.length === 0}
                  data-testid="print-labels-preview-btn"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Imprimir {bicyclesToPrint.length} etiqueta{bicyclesToPrint.length !== 1 ? 's' : ''}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" className="flex-1" onClick={() => setShowFullPreview(false)} data-testid="print-labels-back-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Volver
                </Button>
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  onClick={handlePrint}
                  data-testid="print-labels-confirm-btn"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Confirmar impresión
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
